import { loanDetailsData, periodList } from "configs/loanDetails.config";
import {
  opportunityData,
  OpportunityQueryKey,
} from "configs/opportunity.config";
import merge from "lodash.merge";
import { serializeSubAssets } from "pages/application/opportunities/create/CreateOpportunity";
import { imageModel } from "pages/application/opportunities/create/elements/createOpportunityDetails/CreateOpportunityDetails";
import { useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { LoanDetailsController } from "services/loanDetails/controller";
import {
  DatePicker,
  Dropdown,
  ImageModal,
  Input,
  PhotoGallery,
  Radio,
  RadioGroup,
  Textarea,
  useAccordionWizard,
} from "stories";
import { AccordionForm } from "stories/components/accordionForm";
import { currencyRules, currencyStyle } from "utils/customHooks/currencyFormat";
import { useFileUpload } from "utils/customHooks/useFileUpload";
import { useToast } from "utils/customHooks/useToast";
import { v4 as uuidv4 } from "uuid";
import { removeEmptyValues } from "../borrower/LoanDetailsBorrower";
import { getSubAssetModel, SubAssetType } from "./subAssetType/SubAssetType";

const loanDetailsController = LoanDetailsController();

const getLoanDetailsOverviewModal = () => ({
  amortization_period: {
    count: null,
    unit: "month",
  },
  asset_classes: getSubAssetModel(),
  mortgage_position: "",
  images: [],
  loan_structure: "interest only",
  loan_terms: {
    count: null,
    unit: "month",
  },
  loan_type: "",
  mortgage_broker_fee: "Flat Fee",
  net_amount_requested: "",
  payment_frequency: "monthly",
  percentage: null,
  premium: "",
  fee: "",
  interest_rate: "",
  project_financing_date: null,
  project_highlights: "",
  project_name: "",
  proposed_interest_rate: "fixed",
  refinance_loan: false,
  project_purpose: "",
});
export const LoanDetailsQueryKey = "loan-details";
const LoanDetailsOverview = ({ opportunity }) => {
  const params = useParams();
  const form = useForm({
    defaultValues: getLoanDetailsOverviewModal(),
  });
  const {
    fields: images,
    append: addImage,
    remove: removeImage,
  } = useFieldArray({
    control: form.control,
    name: "images",
  });
  const proposedInterestRate = useWatch({
    control: form.control,
    name: "proposed_interest_rate",
  });
  const queryKey = [LoanDetailsQueryKey, { id: params.id }];
  const [isModalOpen, setModalOpen] = useState(-1);

  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { next } = useAccordionWizard();
  const [loanStructure] = useWatch({
    control: form.control,
    name: ["loan_structure"],
  });

  const { isLoading: fetchLoading } = useQuery({
    queryKey,
    queryFn: loanDetailsController.getOverview.bind(null, params.id),
    onSuccess: (data) => {
      if (data.proposed_interest_rate === null)
        data.proposed_interest_rate = "fixed";
      form.reset(merge(getLoanDetailsOverviewModal(), data));
    },
  });

  const { handleFileFields } = useFileUpload();

  const handleOverviewSubmit = async (values) => {
    if (values.images) {
      for (const image of values.images) {
        delete image.id;
      }
    }
    values.asset_classes = serializeSubAssets(values.asset_classes);
    return handleFileFields(values).then((values) => {
      values.id = params.id;
      values = removeEmptyValues(values);
      return loanDetailsController.updateOverview(values);
    });
  };

  const { mutate, isLoading: postLoading } = useMutation(handleOverviewSubmit, {
    onSuccess: () => {
      showToast("Overview Updated Successfully!", "success");
      next();
      queryClient.invalidateQueries([OpportunityQueryKey, { id: params.id }]);
      queryClient.invalidateQueries(queryKey);
    },
  });

  const handleImagesChange = (images) => {
    form.setValue("images", images);
  };
  const handleSelectedFiles = (files) => {
    Array.from(files).forEach((filename) => {
      addImage({
        key: uuidv4(),
        ...imageModel,
        filename,
      });
    });
    openModal(images.length + files.length - 1);
  };
  const openModal = (index) => {
    setModalOpen(index);
  };
  const {
    formState: { errors },
  } = form;

  return (
    <AccordionForm
      accordionKey="overview"
      accordionTitle="Overview"
      form={form}
      isPostLoading={postLoading}
      isPreLoading={fetchLoading}
      onSubmit={mutate}
    >
      <Row>
        <Col xs={4}>
          <Input label={"Project Name"} {...form.register("project_name")} />
        </Col>
        <Col xs={8}>
          <Textarea
            label={"Project Highlights"}
            {...form.register("project_highlights")}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className={"font-14 mt-4 pb-3"}>Photo Gallery</div>
          <PhotoGallery
            imageList={images}
            removeImageHandler={removeImage}
            uploadImageHandler={handleSelectedFiles}
            onSelectImage={openModal}
          />

          <ImageModal
            visibility={isModalOpen}
            setVisibility={() => setModalOpen(-1)}
            files={images}
            onAccept={handleImagesChange}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={4}>
          <Controller
            name="net_amount_requested"
            control={form.control}
            rules={currencyRules}
            render={({ field }) => {
              return (
                <Input
                  label={"Net Amount Requested"}
                  type="currency"
                  step="0.01"
                  startAdornment="$"
                  {...field}
                  value={field.value || ""}
                  error={errors?.net_amount_requested?.message}
                />
              );
            }}
          />
        </Col>
        <Col xs={4}>
          <Dropdown
            disabled
            label={"Loan Type"}
            list={opportunityData.loanTypeList}
            fullWidth
            {...form.register("loan_type")}
          />
        </Col>
        <Col xs={4} />
      </Row>
      <Row>
        <Col xs={4}>
          <Controller
            control={form.control}
            name="refinance_loan"
            render={({ field }) => {
              const { onChange, ...props } = field;
              return (
                <RadioGroup groupLabel={"Refinance Loan"}>
                  <Radio
                    label={"Yes"}
                    onChange={() => onChange(true)}
                    checked={field.value === true}
                    {...props}
                  />
                  <Radio
                    label={"No"}
                    onChange={() => onChange(false)}
                    checked={field.value === false}
                    {...props}
                  />
                </RadioGroup>
              );
            }}
          />
        </Col>
        <Col>
          <Controller
            control={form.control}
            name="project_purpose"
            render={({ field }) => {
              const { onChange, ...props } = field;
              return (
                <RadioGroup groupLabel={"Project Purpose"}>
                  <Radio
                    label={"For Sale"}
                    onChange={() => onChange("sale")}
                    checked={field.value === "sale"}
                    {...props}
                  />
                  <Radio
                    label={"For Lease"}
                    onChange={() => onChange("lease")}
                    checked={field.value === "lease"}
                    {...props}
                  />
                </RadioGroup>
              );
            }}
          />
        </Col>
        <Col xs={4}>
          <Dropdown
            label={"Mortgage Position"}
            list={opportunityData.financingTypeList}
            fullWidth
            {...form.register("mortgage_position")}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mb-4">
          <div className="label-text">Sub Asset Type</div>
          <div className={`px-4 pb-4 bg-input`}>
            <SubAssetType objKey="asset_classes" />
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={4}>
          <Dropdown
            label={"Loan Structure"}
            list={loanDetailsData.loanStructure}
            fullWidth
            {...form.register("loan_structure")}
          />
        </Col>
        {loanStructure === "interest + principal" && (
          <Col xs={4}>
            <Input
              label={"Amortization Period"}
              type="number"
              step="any"
              {...form.register("amortization_period.count", {
                setValueAs: (v) => Number(parseInt(v)),
                min: {
                  value: 1,
                  message: "This field should be greater than 0",
                },
              })}
              error={errors?.amortization_period?.count?.message}
              containerClassName="pr-0"
              divider
              endAdornment={
                <Dropdown
                  list={periodList}
                  containerClassName="mb-0"
                  interactivity={false}
                  {...form.register("amortization_period.unit", {
                    onChange: (e) => {
                      if (!form.getValues().loan_terms.count) {
                        form.setValue("loan_terms.unit", e.target.value);
                      }
                    },
                  })}
                />
              }
            />
          </Col>
        )}
        <Col />
      </Row>
      <Row>
        <Col xs={4}>
          <Dropdown
            label={"Payment Frequency"}
            list={loanDetailsData.paymentFrequencyList}
            fullWidth
            {...form.register("payment_frequency")}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={4}>
          <Input
            label={"Loan Terms"}
            type="number"
            step="any"
            {...form.register("loan_terms.count", {
              setValueAs: (v) => Number(parseInt(v)) || undefined,
              min: {
                value: 1,
                message: "This field should be greater than 0",
              },
            })}
            error={errors?.loan_terms?.count?.message}
            containerClassName="pr-0"
            divider
            endAdornment={
              <Dropdown
                containerClassName="mb-0"
                interactivity={false}
                list={periodList}
                {...form.register("loan_terms.unit", {
                  onChange: (e) => {
                    if (!form.getValues().amortization_period.count) {
                      form.setValue("amortization_period.unit", e.target.value);
                    }
                  },
                })}
              />
            }
          />
        </Col>
        <Col />
      </Row>
      <Row>
        <Col xs={4}>
          <Dropdown
            label={"Proposed Interest Rate"}
            list={loanDetailsData.interestRate}
            fullWidth
            {...form.register("proposed_interest_rate")}
          />
        </Col>
        <Col xs={4}>
          <Controller
            control={form.control}
            key={proposedInterestRate === "fixed" ? "interest_rate" : "premium"}
            name={
              proposedInterestRate === "fixed" ? "interest_rate" : "premium"
            }
            render={({ field }) => {
              return (
                <Input
                  label={
                    proposedInterestRate === "fixed"
                      ? "Interest Rate"
                      : "Premium"
                  }
                  key={
                    proposedInterestRate === "fixed"
                      ? "Interest Rate"
                      : "Premium"
                  }
                  type="currency"
                  prefix=""
                  startAdornment={
                    proposedInterestRate !== "fixed" && (
                      <span className="overflow-auto">
                        Prime ({currencyStyle(opportunity.prime, 2)}) +
                      </span>
                    )
                  }
                  endDivider
                  endAdornment={<span className="pl-2">%</span>}
                  {...field}
                />
              );
            }}
          />
        </Col>
        <Col />
      </Row>
      <Row>
        <Col xs={4}>
          <Controller
            control={form.control}
            name={`project_financing_date`}
            render={({ field }) => {
              return (
                <DatePicker label={"Projected Financing Date"} {...field} />
              );
            }}
          />
        </Col>
        <Col />
      </Row>
    </AccordionForm>
  );
};

export default LoanDetailsOverview;
